/**
 * The CodeStitch shared-image CDN — the one host family whose photographs are
 * NOT the template's and NOT the client's (SK-0).
 *
 * ## Why this is a module and not a regex at each call site
 *
 * `brand-skin.js`'s `replaceSourceDomainAssets` only reasons about URLs on the
 * template's own host, so a stock photograph is invisible to it and passes
 * straight into every mockup. A composed web-design site shipped two
 * photographs of auto mechanics — `Images/People/mechanic5.jpg` and
 * `mechanic6.jpg` — because they are on this CDN rather than on the template's
 * domain. 110 such images survived across a 39-render audit.
 *
 * The list already existed twice: `CONTENT_CDN_RE` in
 * `parsers/repair-dead-section-images.js` and `CDN_DOMAINS` in
 * `export/asset-resolver.js`. A gate carrying its own copy would be a third,
 * and the first thing to drift.
 *
 * Pure, no imports, Worker-safe.
 */

/** Registrable host suffixes that serve the shared stitch image library. */
export const STOCK_PHOTO_HOST_SUFFIXES = Object.freeze([
  'nyc3.digitaloceanspaces.com',
  'nyc3.cdn.digitaloceanspaces.com'
]);

/**
 * Matches the absolute form used in section HTML. Kept equivalent to
 * `repair-dead-section-images.js`'s `CONTENT_CDN_RE`, which this replaces.
 */
export const STOCK_PHOTO_URL_RE = /^https?:\/\/csimg2?\.nyc3\.(cdn\.)?digitaloceanspaces\.com\//i;

/** True when a URL points at the shared stitch image library. */
export function isStockPhotoUrl(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return false;
  if (STOCK_PHOTO_URL_RE.test(raw)) return true;
  try {
    const host = new URL(raw).hostname.toLowerCase();
    return STOCK_PHOTO_HOST_SUFFIXES.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`)
    );
  } catch {
    return false;
  }
}

/**
 * Bucket a URL for hotlink accounting: which business's server would serve it.
 *
 * @param {string} value
 * @param {string} sourceDomain the template's own domain
 * @returns {'stock-cdn'|'template-source-domain'|'other'|'local'}
 */
export function hotlinkBucket(value, sourceDomain = '') {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw || raw.startsWith('data:') || raw.startsWith('file:') || raw.startsWith('blob:')) {
    return 'local';
  }
  if (isStockPhotoUrl(raw)) return 'stock-cdn';
  const bare = String(sourceDomain || '')
    .toLowerCase()
    .replace(/^www\./, '');
  if (bare) {
    try {
      const host = new URL(raw).hostname.toLowerCase().replace(/^www\./, '');
      if (host === bare || host.endsWith(`.${bare}`)) return 'template-source-domain';
    } catch {
      /* not absolute — falls through to 'other' */
    }
  }
  return 'other';
}
